import { AccessorFn, Column, ColumnDef, Row } from "@tanstack/react-table";
import { SortIcon } from "@/assets/icons";
import type { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import BadgeDataType from "@/components/ui/badge-data-type";
import { normalizeFilterValue } from "@/lib/utils";
import { DataSchemaType } from "@/types/dataset";
import { parseWithDayjs } from "@/utils/date";
import { PreviewData } from "@/hooks/map/usePreviewGeom";
import { Button } from "equator-design-system";

export type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
> & {
  type?: DataSchemaType;
  maxWidth?: number;
  accessorFn?: AccessorFn<TData, TValue>;
};

export type SpatialAnalysisRow = DatasetDetailRow;

export function HeaderCell({
  column,
  title,
  type
}: {
  column: Column<SpatialAnalysisRow>;
  title: string;
  type: DataSchemaType;
}) {
  return (
    <div className="flex flex-col items-start h-full gap-1">
      <div className="flex flex-row items-center w-full gap-2">
        <Button
          variant="ghost"
          className="flex flex-row items-center h-auto p-0 overflow-x-hidden"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          <p
            className="text-xs font-semibold text-txtBody line-clamp-1 text-ellipsis"
            title={title}
          >
            {title}
          </p>
          <SortIcon className="min-w-4" width={16} height={16} />
        </Button>
      </div>

      <div className="flex flex-row items-center gap-2 mt-auto">
        <BadgeDataType type={type} />
      </div>
    </div>
  );
}

export const generateColumns = (
  columnsConfig: { attribute_name: string; data_type: DataSchemaType }[],
  isShowActionMapPreviewEnabled?: boolean,
  handlePreviewClick?: (value: string | null, type: "grid" | "wkt") => void,
  getPreviewDataFromRow?: (row: Row<SpatialAnalysisRow>) => PreviewData
): ExtendedColumnDef<SpatialAnalysisRow, string>[] => {
  const column = columnsConfig.map((col) => {
    const columnDef: ExtendedColumnDef<SpatialAnalysisRow, string> = {
      accessorKey: normalizeFilterValue(col.attribute_name),
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title={col.attribute_name
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")}
          type={col.data_type}
        />
      ),
      cell: ({ cell }) => (
        <p className="text-txtBody text-xs truncate max-w-[200px]">
          {cell.getValue()}
        </p>
      ),
      filterFn: "includesString",
      type: col.data_type,
      enableSorting: true
    };

    if (
      [
        DataSchemaType.DATE,
        DataSchemaType.DATE_TIME,
        DataSchemaType.TIME
      ].includes(col.data_type)
    ) {
      columnDef.sortingFn = (rowA, rowB, columnId) => {
        const dateA = parseWithDayjs(rowA.getValue(columnId));
        const dateB = parseWithDayjs(rowB.getValue(columnId));

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1; // or -1 depending on where you want nulls
        if (!dateB) return -1; // or 1

        return dateA.diff(dateB);
      };
    }

    return columnDef;
  });

  if (isShowActionMapPreviewEnabled) {
    column.push({
      id: "action",
      accessorKey: "action",
      header: () => (
        <p className="text-xs font-semibold text-center text-txtBody">Action</p>
      ),
      cell: (data) => {
        const { value, type } = getPreviewDataFromRow?.(data.row) ?? {
          value: null,
          type: "wkt" as const
        };

        return (
          <Button
            className="w-full h-5 text-blue-500"
            onClick={() => handlePreviewClick?.(value, type)}
            variant="ghost"
            type="button"
          >
            Preview on Map
          </Button>
        );
      },
      size: 160
    });
  }
  return column;
};
